/**
 * Catalog powering the public, indexable mock-exam landing pages
 * (/:lang/mock-exams and /:lang/mock-exams/:examSlug).
 *
 * WHY THIS FILE EXISTS
 * Logami's exam content is its deepest asset and, until now, none of it was
 * reachable by search engines: the simulator lives behind auth and is
 * Disallowed in robots.txt. Competitors (b1class.com, telcb1.com) rank largely
 * on indexable per-exam pages. These pages expose the format, timing and
 * scoring publicly, which is exactly what people search for
 * ("telc b1 modelltest", "goethe b2 modellsatz", "ÖSD C1 Prüfung Aufbau").
 *
 * DATA PROVENANCE — every number below is transcribed from the corresponding
 * base exam JSON in public/data/exams/{provider}/{level}/{provider}-deutsch-
 * {level}-mock-001.json (fields: sections[].sectionName / sectionNameEn /
 * duration / totalPoints, and scoring.passingCriteria). It is duplicated here
 * rather than fetched so the pages prerender to static HTML without a network
 * round-trip. If an exam JSON's format changes, update this file to match —
 * `npm run verify:exam-catalog` checks the two agree.
 *
 * `variants` = number of complete papers we offer for that exam and level.
 *
 * This counts every paper we actually offer. For telc and Goethe that includes the
 * base `*-mock-001.json`: those six were rewritten with our own texts (telc in
 * c52db9a, Goethe tagged logami-original), even though three of them still carry a
 * stale metadata.source naming the publisher PDF. The three ÖSD base papers are
 * ÖSD's own Modelltests, unchanged since import, and are NOT counted, offered or
 * advertised. Papers carrying an excluding `metadata.status` are excluded too.
 * scripts/verify-exam-catalog.js enforces these numbers at build time, because they
 * are published as fact on the public /mock-exams/* pages.
 */

/** Minutes shown as "2 h 45 min" / "45 min". */
export function formatDuration(minutes) {
  if (!minutes || minutes < 1) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * What each publisher calls its own official practice paper.
 *
 * Searchers type "Modelltest" for all three ("telc b2 prüfung modelltest pdf mit
 * lösungen", "goethe c1 modelltest pdf"), but the publishers do not use one word:
 * telc issues Übungstests, the Goethe-Institut Modellsätze, ÖSD Modelltests. The
 * pages use the searched word in headings and the accurate word when they talk
 * about the publisher's own paper.
 */
const PAPER_TERM_DE = { TELC: 'Übungstest', GOETHE: 'Modellsatz', OSD: 'Modelltest' };

/** The publisher's own name for its official practice paper, e.g. 'Übungstest'. */
export function paperTermDe(exam) {
  return PAPER_TERM_DE[exam?.provider] || 'Modelltest';
}

/**
 * Exam pages that exist in German (/de/mock-exams/:slug) as well as English.
 *
 * Single source of truth, read by scripts/prerender.js (prerender + sitemap +
 * hreflang) and src/App.jsx (client hreflang). firebase.json carries the
 * complement (the slugs still redirected) in its /de/mock-exams rule; the
 * prerender fails the build if the two disagree. A slug may only be added once its mockExam.* copy exists in de.js, or
 * the German URL would serve an English body.
 */
export const GERMAN_MOCK_EXAM_SLUGS = ['telc-b1', 'telc-b2', 'telc-c1', 'goethe-b1', 'goethe-b2', 'goethe-c1'];

export const MOCK_EXAM_CATALOG = [
  {
    slug: 'telc-b1',
    seoName: 'telc B1',
    provider: 'TELC',
    providerLabel: 'telc',
    level: 'B1',
    examName: 'telc Deutsch B1 / Zertifikat Deutsch',
    variants: 12,
    totalMinutes: 165,
    totalPoints: 300,
    // TELC/ÖSD grade the written and oral parts separately; both must reach 60%.
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Leseverstehen',          en: 'Reading Comprehension',   minutes: 90, points: 75 },
      { id: 'grammar',   de: 'Sprachbausteine',        en: 'Language Elements',       minutes: 0,  points: 30 },
      { id: 'listening', de: 'Hörverstehen',           en: 'Listening Comprehension', minutes: 30, points: 75 },
      { id: 'writing',   de: 'Schriftlicher Ausdruck', en: 'Written Expression',      minutes: 30, points: 45 },
      { id: 'speaking',  de: 'Mündlicher Ausdruck',    en: 'Oral Expression',         minutes: 15, points: 75 },
    ],
  },
  {
    slug: 'telc-b2',
    seoName: 'telc B2',
    provider: 'TELC',
    providerLabel: 'telc',
    level: 'B2',
    examName: 'telc Deutsch B2',
    variants: 12,
    totalMinutes: 155,
    totalPoints: 300,
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Leseverstehen',          en: 'Reading Comprehension',   minutes: 90, points: 75 },
      { id: 'grammar',   de: 'Sprachbausteine',        en: 'Language Elements',       minutes: 0,  points: 30 },
      { id: 'listening', de: 'Hörverstehen',           en: 'Listening Comprehension', minutes: 20, points: 75 },
      { id: 'writing',   de: 'Schriftlicher Ausdruck', en: 'Written Expression',      minutes: 30, points: 45 },
      { id: 'speaking',  de: 'Mündlicher Ausdruck',    en: 'Oral Expression',         minutes: 15, points: 75 },
    ],
  },
  {
    slug: 'telc-c1',
    // The exam's actual name. People search "telc c1 hochschule" as its own query,
    // and the plain "telc C1" string is still contained in both of these.
    seoName: 'telc C1 Hochschule',
    provider: 'TELC',
    providerLabel: 'telc',
    level: 'C1',
    examName: 'telc Deutsch C1 Hochschule',
    variants: 12,
    totalMinutes: 216,
    totalPoints: 214,
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Leseverstehen',          en: 'Reading Comprehension',   minutes: 90, points: 48 },
      { id: 'grammar',   de: 'Sprachbausteine',        en: 'Language Elements',       minutes: 0,  points: 22 },
      { id: 'listening', de: 'Hörverstehen',           en: 'Listening Comprehension', minutes: 40, points: 48 },
      { id: 'writing',   de: 'Schriftlicher Ausdruck', en: 'Written Expression',      minutes: 70, points: 48 },
      { id: 'speaking',  de: 'Mündlicher Ausdruck',    en: 'Oral Expression',         minutes: 16, points: 48 },
    ],
  },
  {
    slug: 'goethe-b1',
    seoName: 'Goethe B1',
    provider: 'GOETHE',
    providerLabel: 'Goethe-Institut',
    level: 'B1',
    examName: 'Goethe-Zertifikat B1',
    variants: 12,
    totalMinutes: 180,
    totalPoints: 400,
    // Goethe scores each of the four modules independently; modules can be retaken singly.
    passing: 'modular',
    sections: [
      { id: 'reading',   de: 'Lesen',    en: 'Reading Comprehension',   minutes: 65, points: 100 },
      { id: 'listening', de: 'Hören',    en: 'Listening Comprehension', minutes: 40, points: 100 },
      { id: 'writing',   de: 'Schreiben', en: 'Written Expression',     minutes: 60, points: 100 },
      { id: 'speaking',  de: 'Sprechen', en: 'Oral Expression',         minutes: 15, points: 100 },
    ],
  },
  {
    slug: 'goethe-b2',
    seoName: 'Goethe B2',
    provider: 'GOETHE',
    providerLabel: 'Goethe-Institut',
    level: 'B2',
    examName: 'Goethe-Zertifikat B2',
    variants: 12,
    totalMinutes: 195,
    totalPoints: 400,
    passing: 'modular',
    sections: [
      { id: 'reading',   de: 'Lesen',    en: 'Reading',   minutes: 65, points: 100 },
      { id: 'listening', de: 'Hören',    en: 'Listening', minutes: 40, points: 100 },
      { id: 'writing',   de: 'Schreiben', en: 'Writing',  minutes: 75, points: 100 },
      { id: 'speaking',  de: 'Sprechen', en: 'Speaking',  minutes: 15, points: 100 },
    ],
  },
  {
    slug: 'goethe-c1',
    seoName: 'Goethe C1',
    provider: 'GOETHE',
    providerLabel: 'Goethe-Institut',
    level: 'C1',
    examName: 'Goethe-Zertifikat C1',
    variants: 12,
    totalMinutes: 194,
    totalPoints: 400,
    passing: 'modular',
    sections: [
      { id: 'reading',   de: 'Lesen',    en: 'Reading',   minutes: 65, points: 100 },
      { id: 'listening', de: 'Hören',    en: 'Listening', minutes: 40, points: 100 },
      { id: 'writing',   de: 'Schreiben', en: 'Writing',  minutes: 75, points: 100 },
      { id: 'speaking',  de: 'Sprechen', en: 'Speaking',  minutes: 14, points: 100 },
    ],
  },
  {
    slug: 'osd-b1',
    seoName: 'ÖSD B1',
    provider: 'OSD',
    providerLabel: 'ÖSD',
    level: 'B1',
    examName: 'ÖSD Zertifikat Deutsch Österreich B1',
    variants: 4,
    totalMinutes: 175,
    totalPoints: 300,
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Leseverstehen',   en: 'Reading Comprehension',   minutes: 90, points: 75 },
      { id: 'grammar',   de: 'Sprachbausteine', en: 'Language Elements',       minutes: 0,  points: 30 },
      { id: 'listening', de: 'Hören',           en: 'Listening Comprehension', minutes: 30, points: 75 },
      { id: 'writing',   de: 'Schreiben',       en: 'Written Expression',      minutes: 40, points: 45 },
      { id: 'speaking',  de: 'Sprechen',        en: 'Oral Expression',         minutes: 15, points: 75 },
    ],
  },
  {
    slug: 'osd-b2',
    seoName: 'ÖSD B2',
    provider: 'OSD',
    providerLabel: 'ÖSD',
    level: 'B2',
    examName: 'ÖSD Zertifikat B2',
    variants: 6,
    totalMinutes: 230,
    totalPoints: 100,
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Lesen',           en: 'Reading',                 minutes: 90, points: 10 },
      { id: 'grammar',   de: 'Sprachbausteine', en: 'Language Elements',       minutes: 0,  points: 10 },
      { id: 'listening', de: 'Hörverstehen',    en: 'Listening Comprehension', minutes: 30, points: 20 },
      { id: 'writing',   de: 'Schreiben',       en: 'Written Expression',      minutes: 90, points: 30 },
      { id: 'speaking',  de: 'Sprechen',        en: 'Speaking',                minutes: 20, points: 30 },
    ],
  },
  {
    slug: 'osd-c1',
    seoName: 'ÖSD C1',
    provider: 'OSD',
    providerLabel: 'ÖSD',
    level: 'C1',
    examName: 'ÖSD Zertifikat C1',
    variants: 6,
    totalMinutes: 240,
    totalPoints: 100,
    passing: 'separate',
    sections: [
      { id: 'reading',   de: 'Lesen',           en: 'Reading',   minutes: 90, points: 15 },
      { id: 'grammar',   de: 'Sprachbausteine', en: 'Language Elements', minutes: 0, points: 5 },
      { id: 'listening', de: 'Hören',           en: 'Listening', minutes: 40, points: 20 },
      { id: 'writing',   de: 'Schreiben',       en: 'Writing',   minutes: 90, points: 30 },
      { id: 'speaking',  de: 'Sprechen',        en: 'Speaking',  minutes: 20, points: 30 },
    ],
  },
];

export const MOCK_EXAM_SLUGS = MOCK_EXAM_CATALOG.map((e) => e.slug);

export function getMockExam(slug) {
  if (!slug) return null;
  return MOCK_EXAM_CATALOG.find((e) => e.slug === String(slug).toLowerCase().trim()) || null;
}
